import React, { useState, useEffect } from 'react';
import { useAppData } from '../../../../../../context/AppDataContext';
import { Dialog, DialogContent } from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import inventoryServices from '../inventoryServices';
import { useAlert } from '../../../../../../context/AlertContext';
import { MoveToInbox, Outbox } from '@mui/icons-material';

function InventoryAdjustmentForm({ selectedInventory, callBack,setSelectedInventory }) {
    const { productDD, getDDDataById } = useAppData();
    const [item, setItem] = useState(null);
    const [error, setError] = useState("");

    const { AppAlert } = useAlert();
    const [savingStatus, setSavingStatus] = useState(false);

    useEffect(() => {
        if (selectedInventory) {
            let _productDetails = getDDDataById(productDD, selectedInventory.productId);

            let _item = {}; 
            _item['companyId'] = selectedInventory.companyId || 1; 
            _item['locationId'] = selectedInventory.locationId;
            _item['productId'] = selectedInventory.productId;
            _item['title'] = _productDetails?.label || "Unknown Product";
            
            _item['cost'] = _productDetails?.cost || selectedInventory.price || 0;
            _item['price'] = _productDetails?.price || 0;
            _item['mrp'] = _productDetails?.mrp || 0;

            _item['adjustmentType'] = "add"; 
            _item['qty'] = 1; 
            _item['available'] = Number(selectedInventory.qty) || 0; 
            _item['narration'] = "";
            
            setItem(_item);
            setError(""); 
        }
    }, [selectedInventory, productDD]);

    const validateForm = (updatedItem) => {
        const inputQty = Number(updatedItem.qty);
        const inputCost = Number(updatedItem.cost);

        if (!updatedItem.adjustmentType) {
            setError("Please select an adjustment direction.");
            return false;
        }
        if (isNaN(inputQty) || inputQty <= 0) {
            setError("Quantity must be a positive integer greater than zero.");
            return false;
        }
        if (isNaN(inputCost) || inputCost < 0) {
            setError("Adjustment rate cannot be negative.");
            return false;
        }
        
        if (updatedItem.adjustmentType === 'remove' && inputQty > updatedItem.available) {
            setError(`Insufficient Stock: Cannot remove ${inputQty} units. Only ${updatedItem.available} units are present.`);
            return false;
        }

        // REQUIRED REMARK VALIDATION
        if (!updatedItem.narration || !updatedItem.narration.trim()) {
            setError("Narration/remarks are required for audit trail purposes.");
            return false;
        }

        setError("");
        return true;
    };

    const handleQtyChange = (val) => {
        setItem(prev => {
            const updated = { ...prev, qty: val };
            validateForm(updated);
            return updated;
        });
    };

    const handleCostChange = (val) => {
        setItem(prev => {
            const updated = { ...prev, cost: val };
            validateForm(updated);
            return updated;
        });
    };

    const handleTypeChange = (type) => {
        setItem(prev => {
            const updated = { ...prev, adjustmentType: type };
            validateForm(updated);
            return updated;
        });
    };

    const handleNarrationChange = (val) => {
        setItem(prev => {
            const updated = { ...prev, narration: val };
            validateForm(updated);
            return updated;
        });
    };

    const handleClose = () => {
        setItem(null);
        setSelectedInventory(null);
        setError("");
    };

    const adjustItem = () => {
        if (!validateForm(item)) return;

        setSavingStatus(true);

        const payload = {
            companyId: parseInt(item.companyId, 10),
            locationId: parseInt(item.locationId, 10),
            productId: parseInt(item.productId, 10),
            qty: parseInt(item.qty, 10),
            price: parseFloat(item.cost), 
            adjustmentType: item.adjustmentType, 
            narration: item.narration.trim()
        };

        inventoryServices.adjustStock(payload)
            .then(res => {
                if (res.error) {
                    AppAlert(res.msg, "error");
                } else {
                    AppAlert(res.msg || "Inventory updated successfully.", "success");
                    handleClose();
                    if (callBack) callBack();
                }
            })
            .catch(err => {
                console.error(err);
                AppAlert(err?.response?.data?.msg || err.msg || "Network transaction failure.", "error");
            })
            .finally(() => {
                setSavingStatus(false);
            });
    };

    const currentQty = item ? Number(item.available) : 0;
    const adjustQty = item ? (Number(item.qty) || 0) : 0;
    const projectedQty = item 
        ? (item.adjustmentType === 'add' ? currentQty + adjustQty : currentQty - adjustQty)
        : 0;

    // Fast check to see if the textarea is currently empty
    const isNarrationEmpty = !item?.narration || !item.narration.trim();

    return (
        <Dialog open={item !== null} onClose={handleClose} fullWidth maxWidth="md">
            <DialogContent className="pt-4">
                {item && (
                    <div className="p-1">
                        {/* Header Product Details Banner */}
                        <div className="p-3 bg-light rounded border mb-4">
                            <small className="text-muted text-uppercase fw-semibold d-block tracking-wider mb-1">Selected Product</small>
                            <h5 className="text-dark fw-bold mb-0">{item.title}</h5>
                        </div>

                        <div className="row g-3">
                            {/* 1. STRUCTURAL DIRECTION SELECTOR VIA MUI SYSTEM ICONS */}
                            <div className="col-md-12">
                                <label className="form-label fw-semibold text-secondary">Adjustment Direction</label>
                                <div className="row g-3">
                                    <div className="col-6">
                                        <div 
                                            className={`p-3 rounded border d-flex align-items-center justify-content-between user-select-none transition-all ${item.adjustmentType === 'add' ? 'bg-success-subtle border-success text-success fw-bold shadow-sm' : 'bg-white text-muted border-light-subtle'}`}
                                            onClick={() => !savingStatus && handleTypeChange('add')}
                                            style={{ cursor: 'pointer', borderWidth: '2px' }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                {item.adjustmentType === 'add' ? <RadioButtonCheckedIcon color="success" /> : <RadioButtonUncheckedIcon />}
                                                <div>
                                                    <div className="text-uppercase tracking-wide small">Stock Found (Add)</div>
                                                    <span className="small fw-normal text-muted-50">Increments warehouse totals</span>
                                                </div>
                                            </div>
                                            <span className="fs-4"><MoveToInbox sx={{fontSize:36}} /></span>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div 
                                            className={`p-3 rounded border d-flex align-items-center justify-content-between user-select-none transition-all ${item.adjustmentType === 'remove' ? 'bg-danger-subtle border-danger text-danger fw-bold shadow-sm' : 'bg-white text-muted border-light-subtle'}`}
                                            onClick={() => !savingStatus && handleTypeChange('remove')}
                                            style={{ cursor: 'pointer', borderWidth: '2px' }}
                                        >
                                            <div className="d-flex align-items-center gap-2">
                                                {item.adjustmentType === 'remove' ? <RadioButtonCheckedIcon color="error" /> : <RadioButtonUncheckedIcon />}
                                                <div>
                                                    <div className="text-uppercase tracking-wide small">Shortage / Damaged (Remove)</div>
                                                    <span className="small fw-normal text-muted-50">Decrements warehouse totals</span>
                                                </div>
                                            </div>
                                            <span className="fs-4"><Outbox sx={{fontSize:36}} /></span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. QUANTITY INPUT FIELD INTEGRATED WITH INLINE PROJECTED IMPACT PANEL */}
                            <div className="col-md-12 mt-4">
                                <label className="form-label fw-semibold text-secondary">Adjust Quantity</label>
                                <div className="input-group input-group-lg">
                                    <span className="input-group-text bg-light text-secondary fw-semibold border-end-0">
                                        Current: {currentQty}
                                    </span>
                                    <span className={`input-group-text fw-bold border-start-0 border-end-0 ${item.adjustmentType === 'add' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'}`}>
                                        {item.adjustmentType === 'add' ? '＋' : '—'}
                                    </span>
                                    <input 
                                        className={`form-control fw-bold ${error && error.includes('Quantity') ? 'is-invalid' : ''}`}
                                        type='number' 
                                        min="1"
                                        value={item.qty} 
                                        onChange={(e) => handleQtyChange(e.target.value)}
                                        disabled={savingStatus}
                                        placeholder="Enter adjustment qty..."
                                    />
                                    <span className="input-group-text bg-light text-dark border-start-0 font-monospace">
                                        ➔
                                    </span>
                                    <span className={`input-group-text fw-bold border-start-0 text-white ${item.adjustmentType === 'add' ? 'bg-success border-success' : 'bg-danger border-danger'}`}>
                                        After Update: {projectedQty} Units
                                    </span>
                                </div>
                            </div>

                            {/* 3. COST RATE FIELD */}
                            <div className="col-md-6 mt-4">
                                <label className="form-label fw-semibold text-secondary">Adjustment Rate (Unit Cost)</label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">₹</span>
                                    <input 
                                        className='form-control fw-semibold' 
                                        type='number' 
                                        step="0.01"
                                        min="0"
                                        value={item.cost} 
                                        onChange={(e) => handleCostChange(e.target.value)} 
                                        placeholder="0.00"
                                        disabled={savingStatus}
                                    />
                                </div>
                            </div>

                            {/* 4. TOTAL VALUATION SUMMARY PANEL */}
                            <div className="col-md-6 mt-4">
                                <label className="form-label fw-semibold text-secondary">Calculated Sub-Total</label>
                                <div className="form-control bg-light fw-bold text-dark text-end border-dashed p-2 fs-5">
                                    ₹{((Number(item.qty) || 0) * (Number(item.cost) || 0)).toFixed(2)}
                                </div>
                            </div>

                            {/* 5. NARRATION NOTES COMPONENT (NOW REQUIRED) */}
                            <div className="col-md-12 mt-3">
                                <label className="form-label fw-semibold text-secondary d-flex justify-content-between">
                                    <span>Adjustment Narration / Remarks <span className="text-danger">*</span></span>
                                    {isNarrationEmpty && <small className="text-danger fw-normal">Please provide a reason</small>}
                                </label>
                                <textarea
                                    className={`form-control ${error && isNarrationEmpty ? 'is-invalid' : ''}`}
                                    rows="2"
                                    value={item.narration}
                                    onChange={(e) => handleNarrationChange(e.target.value)}
                                    placeholder="Enter physical verification notes, audit reference, or explanation..."
                                    disabled={savingStatus}
                                />
                            </div>

                            {/* Consolidated Form Error Display */}
                            {error && (
                                <div className="col-md-12 mt-2">
                                    <div className="text-danger small fw-semibold">⚠️ {error}</div>
                                </div>
                            )}
                        </div>

                        {/* SUBMISSION INTERACTION BUTTON BLOCK */}
                        <div className="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
                            <button 
                                className="btn btn-light border me-2 px-3 fw-semibold text-muted" 
                                onClick={handleClose}
                                disabled={savingStatus}
                            >
                                Cancel
                            </button>
                            <button 
                                className='btn btn-primary px-4 fw-bold shadow-sm d-flex align-items-center gap-2' 
                                onClick={adjustItem}
                                disabled={savingStatus || !!error || !item.qty || Number(item.qty) <= 0 || isNarrationEmpty}
                            >
                                {savingStatus ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Processing...
                                    </>
                                ) : (
                                    'Submit Adjustment'
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default InventoryAdjustmentForm;